import {
    ColumnFiltersState,
    ColumnOrderState,
    PaginationState,
    SortingState,
    VisibilityState,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { useTableColumnSettings } from '@/cip/widgets/Table/useTableColumnSettings';

interface UseAccountTableStateOptions {
    tableId: string;
    accountGUID: string;
    excludeColumns?: string[];
    defaultPageSize?: number;
    defaultColumnVisibility?: VisibilityState;
    defaultColumnOrder?: ColumnOrderState;
    initialColumnFilters?: ColumnFiltersState;
}

export const useAccountTableState = ({
    tableId,
    accountGUID,
    excludeColumns = [],
    defaultPageSize = 20,
    defaultColumnVisibility,
    defaultColumnOrder,
    initialColumnFilters = [],
}: UseAccountTableStateOptions) => {
    const {
        columnVisibility,
        setColumnVisibility,
        columnOrder,
        setColumnOrder,
        columnSizing,
        setColumnSizing,
        resetSettings,
    } = useTableColumnSettings(tableId, {
        defaultColumnVisibility,
        defaultColumnOrder,
    });

    const columnVisibilityWithExclusions = useMemo(
        () => ({
            ...columnVisibility,
            ...Object.fromEntries(excludeColumns.map((col) => [col, false])),
        }),
        [columnVisibility, excludeColumns],
    );

    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: defaultPageSize,
    });

    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialColumnFilters);

    const accountFilter = useMemo(() => [{ id: 'portfolioGuid', value: [accountGUID] }], [accountGUID]);

    const allFilters = useMemo(() => [...accountFilter, ...columnFilters], [accountFilter, columnFilters]);

    return {
        // Column settings
        columnVisibility: columnVisibilityWithExclusions,
        setColumnVisibility,
        columnOrder,
        setColumnOrder,
        columnSizing,
        setColumnSizing,
        resetSettings,

        // Table state
        pagination,
        setPagination,
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,

        // Filters
        accountFilter,
        allFilters,
    };
};
